/**
 * useDiscussionStream — single React-facing seam between the SSE pipeline and the store.
 * Invariants: never reads the full store; subscribes via shallow selectors; aborts on unmount.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDebateStore } from "../store/debateStore";
import { shallow } from "../store/selectors";
import type {
  DebateEvent,
  DebateTopology,
  SessionStatus,
  UserGestureToken,
} from "../domain/types";
import type { DebateStartOptions } from "../store/slices/sessionSlice";
import {
  setTelemetrySession,
  trackDebate,
  trackError,
  trackSSE,
  trackStoreTransition,
} from "../transport/telemetryService";

const DEFAULT_ENDPOINT = "/api/debate";

export interface UseDiscussionStreamApi {
  status: SessionStatus;
  start(
    query: string,
    topology: DebateTopology,
    gesture: UserGestureToken,
    opts?: DebateStartOptions,
  ): Promise<void>;
  /** İş Kolu — arka planda devam eden tartışma. See `sessionSlice.resume`. */
  resume(sessionId: string): Promise<void>;
  cancel(reason?: string): void;
  interrupt(): void;
  isInterrupted: boolean;
  error: Error | null;
}

interface SessionLens {
  status: SessionStatus;
  isInterrupted: boolean;
  error: Error | null;
}

const sessionSelector = (
  s: ReturnType<typeof useDebateStore.getState>,
): SessionLens => ({
  status: s.status,
  isInterrupted: s.interruptActive,
  error: s.error,
});

export function useDiscussionStream(
  endpoint: string = DEFAULT_ENDPOINT,
): UseDiscussionStreamApi {
  const lens = useDebateStore(sessionSelector, shallow);
  const [, setTick] = useState(0); // bump on transient hook errors

  const handlerRef = useRef<((evt: DebateEvent) => void) | null>(null);
  const errorRef = useRef<((err: Error) => void) | null>(null);

  const dispatch = useCallback((evt: DebateEvent) => {
    const s = useDebateStore.getState();
    trackSSE(`sse.${evt.type}`, { turnId: "turnId" in evt ? evt.turnId : undefined });
    switch (evt.type) {
      case "persona.hydrate":
        s.hydratePersonas({ personas: evt.personas, seatOrder: evt.seatOrder });
        trackStoreTransition("personas.hydrated", { count: evt.seatOrder.length });
        return;
      case "turn.start":
        s.startTurn({
          id: evt.turnId,
          speakerId: evt.speakerId,
          roundIndex: evt.roundIndex,
          phase: "retrieving",
          critique: null,
        });
        s.bumpRound(evt.roundIndex);
        return;
      case "turn.phase":
        s.setTurnPhase(evt.turnId, evt.phase);
        return;
      case "turn.critique":
        s.attachCritique(evt.turnId, evt.critique);
        s.setTurnPhase(evt.turnId, "speaking");
        return;
      case "turn.token":
        s.appendToken(evt.turnId, evt.token);
        return;
      case "turn.end": {
        trackDebate("turn.end", {
          turnId: evt.turnId,
          outcome: evt.outcome,
          textLen: evt.finalText?.length ?? 0,
          forfeitReason: evt.forfeitReason,
        });
        s.setConsensusPending(true);
        // Hydration fallback: if the live `turn.rag` was missed (e.g. SSE
        // reconnect mid-turn), the `turn.end` mirror still carries the
        // citations. attachRagBatch dedupes by fingerprint so a fresh
        // run with both events present is safe.
        if (evt.ragCitations && evt.ragCitations.length > 0) {
          s.attachRagBatch(evt.turnId, {
            query: "",
            citations: evt.ragCitations,
          });
        }
        // Amendment #3: 'forfeited' is a graceful transcript outcome, never an error.
        if (evt.outcome === "forfeited") {
          s.endTurn(
            evt.turnId,
            evt.finalText,
            "forfeited",
            evt.forfeitReason ?? "max retries exceeded",
            evt.citationAudit,
          );
        } else {
          s.endTurn(evt.turnId, evt.finalText, "spoke", undefined, evt.citationAudit);
        }
        return;
      }
      case "turn.research":
        s.attachResearch(evt.turnId, {
          query: evt.query,
          sources: evt.sources,
          elapsedMs: evt.elapsedMs,
          cacheHit: evt.cacheHit ?? false,
        });
        return;
      case "turn.rag":
        s.attachRagBatch(evt.turnId, {
          query: evt.query,
          citations: evt.citations,
        });
        return;
      case "turn.thinking":
        s.attachThinking(evt.turnId, {
          chars: evt.chars,
          summary: evt.summary,
        });
        return;
      case "consensus.update":
        s.pushConsensus(evt.snapshot);
        return;
      case "moderator.interrupt":
        s.triggerInterrupt(evt.pivot);
        return;
      case "moderator.resolve":
        s.resolveInterrupt();
        return;
      case "debate.finalize": {
        trackDebate("debate.finalize", { reason: evt.reason, turns: s.turnOrder.length });
        if (s.turnOrder.length === 0 && s.status !== "running") {
          // eslint-disable-next-line no-console
          console.warn(
            "[stream] dropping debate.finalize before any turn observed",
            { reason: evt.reason, status: s.status },
          );
          return;
        }
        // Persist the reason BEFORE flipping status so any reactive
        // selector that observes `status === "finalized"` already sees
        // the reason (avoids a single-frame "finalized but reason=null"
        // flicker on the round counter pill).
        s.setFinalizeReason(evt.reason);
        s.setStatus("finalized");
        return;
      }
      case "verdict.ready": {
        // Same defensive invariant as debate.finalize above. A
        // verdict.ready that fires while we have zero turns and the
        // session is still in `provisioning`/`draft` is never a real
        // verdict — it's leakage from an older session.
        if (s.turnOrder.length === 0 && s.status !== "running") {
          // eslint-disable-next-line no-console
          console.warn(
            "[stream] dropping verdict.ready before any turn observed",
            { status: s.status },
          );
          return;
        }
        s.setVerdict(evt.verdict);
        s.setStatus("finalized");
        return;
      }
      case "stream.error": {
        const err = new Error(`[${evt.code}] ${evt.message}`);
        trackError("stream.error", err.message);
        s.setError(err);
        return;
      }
      case "user.interject":
        s.ingestServerInterjection({
          type: "user.interject",
          id: evt.id,
          text: evt.text,
          receivedAt: evt.receivedAt,
          appliedAtRound: evt.appliedAtRound,
          status: evt.status,
        });
        return;
      // Konsey Cognitive Renaissance — Bölüm A2.
      // Sycophancy alert: FE banner için bir custom event dispatch et;
      // store'da ayrı bir slice tanımlayana kadar window event'i yeterli.
      case "sycophancy.alert": {
        if (typeof window !== "undefined") {
          try {
            window.dispatchEvent(
              new CustomEvent("consensus:sycophancy-alert", {
                detail: {
                  round: evt.round,
                  score: evt.score,
                  dispersion: evt.dispersion,
                  consecutive: evt.consecutive,
                  devilAdvocateInvited: evt.devil_advocate_invited ?? false,
                },
              }),
            );
          } catch {
            /* ignore — toast/banner is best-effort */
          }
        }
        return;
      }
      // Konsey Cognitive Renaissance — Bölüm D2 + D4.
      // Brain snapshot doğrudan brainSlice'a yazılır; modal açıkken
      // canlı update sağlar. Ek window event de dispatch edilir
      // (analytics / dış dinleyiciler için).
      case "brain.update": {
        try {
          // SSE evt.snapshot Record<string,unknown>; brainSlice şeması
          // tüm alanları number/string olarak bekler. Backend Pydantic
          // model_dump zaten doğru şekli üretir; runtime check yerine
          // unknown cast → typed cast (tek shot).
          const snap = evt.snapshot as unknown as Parameters<
            typeof s.applyBrainSnapshot
          >[0];
          if (snap && snap.persona_id) {
            s.applyBrainSnapshot(snap);
          }
        } catch {
          /* ignore — soft-degrade */
        }
        if (typeof window !== "undefined") {
          try {
            window.dispatchEvent(
              new CustomEvent("consensus:brain-update", {
                detail: {
                  personaId: evt.personaId,
                  turnIndex: evt.turnIndex,
                  snapshot: evt.snapshot,
                },
              }),
            );
          } catch {
            /* ignore */
          }
        }
        return;
      }
      case "resilience.progress": {
        if (typeof window !== "undefined") {
          try {
            window.dispatchEvent(
              new CustomEvent("consensus:resilience-progress", {
                detail: evt,
              }),
            );
          } catch {
            /* ignore */
          }
        }
        return;
      }
      // ── Kalite Planı — additive event'ler → qualitySlice ─────────────
      // Hepsi opsiyoneldir: backend göndermezse slice boş kalır ve
      // hiçbir tüketici bileşen (rozet/band/panel) render edilmez.
      case "turn.plan":
        s.attachTurnPlan(evt.turnId, evt.plan);
        return;
      case "turn.verify":
        s.attachTurnVerify(evt.turnId, {
          verdict: evt.verdict,
          healJumps: evt.healJumps,
        });
        return;
      case "subagent.result":
        s.addSubagentResult(evt.kind, evt.result);
        return;
      case "moderator.summon":
        s.addModeratorSummon({
          witnessId: evt.witnessId,
          witnessName: evt.witnessName,
          requirement: evt.requirement,
        });
        return;
      case "coverage.update":
        s.applyCoverageUpdate(evt.matrix);
        return;
      case "entity.graph":
        s.applyEntityGraph(evt.graph);
        return;
      case "investigation.plan":
        s.applyInvestigationPlan(evt.plan);
        return;
      default: {
        // Exhaustiveness guard — adding a new event variant must update this switch.
        const _exhaustive: never = evt;
        return _exhaustive;
      }
    }
  }, []);

  const handleError = useCallback((err: Error) => {
    trackError("sse.transport_error", err.message, err.stack);
    useDebateStore.getState().setError(err);
    setTick((n) => n + 1);
  }, []);

  // Keep refs up-to-date so the configureTransport closure always sees latest dispatch.
  useEffect(() => {
    handlerRef.current = dispatch;
    errorRef.current = handleError;
  }, [dispatch, handleError]);

  // Configure transport once — endpoint changes re-register.
  useEffect(() => {
    useDebateStore.getState().configureTransport({
      endpoint,
      onEvent: (evt) => handlerRef.current?.(evt),
      onError: (err) => errorRef.current?.(err),
    });
  }, [endpoint]);

  // NO unmount-cleanup abort:
  //
  // Earlier versions aborted the in-flight AbortController on hook
  // unmount. That's a footgun in this codebase because `useStartDebate`
  // ALSO instantiates `useDiscussionStream`, and the wizard mounts/un-
  // mounts dynamically. On the happy "Tartışmayı Başlat → closeWizard"
  // path the wizard's hook instance unmounts BEFORE the SSE socket has
  // exchanged its first frame, and its cleanup callback then aborts
  // the controller that the consensus page just installed — producing
  // the "stream closed without verdict or turns" symptom 22ms after
  // the click.
  //
  // The session lifecycle owns its AbortController explicitly via
  // `cancel()` / `resetForNew()` / new `start()`. Hook unmount is not a
  // session boundary; if the user navigates away the SPA tear-down
  // closes the fetch socket anyway. Leaving the controller alone here
  // means a wizard remount can never tear down a debate that the rest
  // of the UI is still observing.

  const start = useCallback(
    async (
      query: string,
      topology: DebateTopology,
      gesture: UserGestureToken,
      opts?: DebateStartOptions,
    ): Promise<void> => {
      trackDebate("debate.start_requested", { query: query.slice(0, 100), topology });
      const t0 = performance.now();
      try {
        await useDebateStore.getState().start(query, topology, gesture, opts);
        const sid = useDebateStore.getState().sessionId;
        if (sid) setTelemetrySession(sid);
        trackDebate("debate.started", { sessionId: sid }, "info");
      } catch (e) {
        trackError(
          "debate.start_failed",
          e instanceof Error ? e.message : String(e),
          e instanceof Error ? e.stack : undefined,
        );
        throw e;
      } finally {
        trackDebate("debate.start_duration", { durationMs: Math.round(performance.now() - t0) });
      }
    },
    [],
  );

  const resume = useCallback(async (sessionId: string): Promise<void> => {
    trackDebate("debate.resume_requested", { sessionId });
    try {
      await useDebateStore.getState().resume(sessionId);
      setTelemetrySession(sessionId);
      trackDebate("debate.resumed", { sessionId }, "info");
    } catch (e) {
      trackError(
        "debate.resume_failed",
        e instanceof Error ? e.message : String(e),
        e instanceof Error ? e.stack : undefined,
      );
      throw e;
    }
  }, []);

  const cancel = useCallback((reason?: string): void => {
    trackDebate("debate.cancelled", { reason });
    useDebateStore.getState().cancel(reason);
  }, []);

  const interrupt = useCallback((): void => {
    useDebateStore.getState().triggerInterrupt("user-requested");
  }, []);

  return useMemo(
    (): UseDiscussionStreamApi => ({
      status: lens.status,
      isInterrupted: lens.isInterrupted,
      error: lens.error,
      start,
      resume,
      cancel,
      interrupt,
    }),
    [lens.status, lens.isInterrupted, lens.error, start, resume, cancel, interrupt],
  );
}
